import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  movementIsOutbound,
  movementMagnitude,
  tInventory,
  type StockMovement,
} from "@/features/staff/inventory/inventory"
import { formatDateTime } from "@/lib/formatters"

/**
 * One supply's ledger, as a table.
 *
 * Lives here rather than in either screen because both render it: the full
 * supply page and the drawer the list opens. Two copies of a five-column ledger
 * is two places for the balance column to drift out of `tabular-nums`.
 *
 * ## It is read-only by construction
 *
 * There is no edit control on any row and no endpoint behind one either —
 * `Location::StockMovement` is `readonly?` once persisted. The answer to a row
 * written wrong is a count correction, which is a **new row in the opposite
 * direction**, and both stay visible. That is the part that matters when the
 * question is whether stock walked out or was miscounted.
 */
export function MovementsTable({
  movements,
  unitLabel,
  timeZone,
  "data-testid": testId,
}: {
  movements: StockMovement[]
  unitLabel: string
  timeZone: string | null
  "data-testid"?: string
}) {
  return (
    <Table data-testid={testId}>
      <TableHeader>
        <TableRow>
          <TableHead>{tInventory("ledger_when")}</TableHead>
          <TableHead>{tInventory("ledger_reason")}</TableHead>
          {/* The unit is a property of the supply, not of the row: this whole
              table is one supply, so it is stated once here instead of being
              repeated down every line of a ledger that can run to two hundred. */}
          <TableHead className="text-right">
            {tInventory("ledger_quantity_in", { unit: unitLabel })}
          </TableHead>
          <TableHead className="text-right">{tInventory("ledger_balance")}</TableHead>
          <TableHead>{tInventory("ledger_note")}</TableHead>
          <TableHead>{tInventory("ledger_actor")}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {movements.map((movement) => {
          const outbound = movementIsOutbound(movement.quantity)

          return (
            <TableRow key={movement.id} data-testid={`movement-row-${movement.id}`}>
              <TableCell className="text-muted-foreground whitespace-nowrap text-xs tabular-nums">
                {formatDateTime(movement.createdAt, { timeZone: timeZone ?? undefined })}
              </TableCell>

              <TableCell>
                <span className="flex items-center gap-2">
                  {/* The dot rule: the tone lives in a size-2 mark, the label
                      stays on --foreground. Outbound is not an error, so it is
                      `occupied` rather than destructive — most of this ledger is
                      food being cooked. */}
                  <span
                    aria-hidden="true"
                    className={
                      outbound
                        ? "bg-status-occupied size-2 shrink-0 rounded-full"
                        : "bg-status-available size-2 shrink-0 rounded-full"
                    }
                  />
                  <span data-testid={`movement-${movement.id}-reason`}>{movement.reasonLabel}</span>
                </span>
              </TableCell>

              <TableCell
                className="text-foreground whitespace-nowrap text-right font-semibold tabular-nums"
                data-testid={`movement-${movement.id}-quantity`}
              >
                {outbound ? "−" : "+"}
                {movementMagnitude(movement.quantity)}
              </TableCell>

              <TableCell
                className="text-muted-foreground text-right tabular-nums"
                data-testid={`movement-${movement.id}-balance`}
              >
                {movement.balanceAfter}
              </TableCell>

              <TableCell className="text-muted-foreground text-xs">{movement.note}</TableCell>

              {/* Never blank: a movement nobody signed was written by a job or by
                  a status change with no session, and an empty cell reads as
                  missing data rather than as "el sistema". */}
              <TableCell className="text-muted-foreground whitespace-nowrap text-xs">
                {movement.actorName ?? tInventory("ledger_system")}
              </TableCell>
            </TableRow>
          )
        })}
      </TableBody>
    </Table>
  )
}
